import React, { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useLanguage } from '../contexts/LanguageContext'
import {
    Avatar,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Container,
    Grid,
    Stack,
    Typography,
} from '@mui/material'
import {
    ArrowForward as ArrowForwardIcon,
    AutoStories as StoriesIcon,
    CalendarMonth as CalendarIcon,
    TrendingUp as TrendIcon,
} from '@mui/icons-material'

import heroWhy from '../assets/blog/why-mojocash-hero.png'
import heroNoMiddle from '../assets/blog/no-middleman.jpg'

const ORANGE = '#FF8A00'
const cardSx = {
    borderRadius: 6,
    background: 'rgba(16, 11, 38, 0.92)',
    border: '1px solid rgba(255,255,255,0.08)',
    boxShadow: '0 24px 60px rgba(0,0,0,0.22)',
}

const Blog = () => {
    const navigate = useNavigate()
    const { lang } = useLanguage()
    const isFr = lang === 'fr'

    const posts = useMemo(
        () => [
            {
                slug: 'why-mojopayr-app',
                category: 'PRODUCT',
                title: isFr
                    ? 'Pourquoi MojoPayr est concu pour les vrais paiements au pays'
                    : 'Why MojoPayr is built for real back-home payments',
                excerpt: isFr
                    ? 'MojoPayr est concu autour de la vraie maniere dont les familles de la diaspora et les utilisateurs locaux font circuler l argent : change clair, paiements directs, preuves, et moins d intermediaires.'
                    : 'MojoPayr is designed around how diaspora families and local users actually move money: clear FX, direct payments, proof, and fewer middle people.',
                date: 'Mar 29, 2026',
                hero: heroWhy,
                tag: isFr ? 'A la une' : 'Featured',
            },
            {
                slug: 'no-more-middle-person-payments',
                category: 'PAYMENTS',
                title: isFr
                    ? 'Fini les intermediaires pour les services et le soutien familial'
                    : 'No more middle person for services and family support',
                excerpt: isFr
                    ? 'Les paiements directs aux commerces, le reglement des factures, et des pistes de preuve plus propres rendent le soutien au pays plus fiable et plus facile a suivre.'
                    : 'Direct business payments, bill pay, and cleaner proof trails make support back home more reliable and easier to track.',
                date: 'Mar 12, 2026',
                hero: heroNoMiddle,
                tag: isFr ? 'Populaire' : 'Popular',
            },
        ],
        [isFr]
    )

    const featured = posts[0]
    const latest = posts.slice(1)

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#09061B', py: { xs: 4, md: 6 } }}>
            <Container maxWidth="lg">
                <Card
                    sx={{
                        ...cardSx,
                        mb: 3,
                        overflow: 'hidden',
                        background:
                            'linear-gradient(135deg, rgba(17,10,40,0.98) 0%, rgba(70,36,188,0.96) 58%, rgba(255,138,0,0.16) 100%)',
                    }}
                >
                    <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                        <Stack spacing={1.5}>
                            <Stack direction="row" spacing={1.2} alignItems="center">
                                <Avatar sx={{ width: 42, height: 42, bgcolor: 'rgba(255,138,0,0.12)', border: '1px solid rgba(255,138,0,0.24)' }}>
                                    <StoriesIcon sx={{ color: ORANGE }} />
                                </Avatar>
                                <Chip label={isFr ? 'Analyses MojoPayr' : 'MojoPayr Insights'} sx={{ bgcolor: 'rgba(255,255,255,0.12)', color: '#fff', fontWeight: 900 }} />
                            </Stack>
                            <Typography component="h1" sx={{ color: '#fff', fontWeight: 950, fontSize: { xs: 30, md: 44 } }}>
                                Blog
                            </Typography>
                            <Typography sx={{ color: 'rgba(255,255,255,0.80)', maxWidth: 860 }}>
                                {isFr
                                    ? 'Vision produit, tendances de paiement, analyses diaspora, et histoires pratiques sur les transferts d argent, les paiements aux commerces, et le soutien plus direct aux proches au pays.'
                                    : 'Product thinking, payment trends, diaspora insights, and practical stories about moving money, paying businesses, and supporting people back home more directly.'}
                            </Typography>
                        </Stack>
                    </CardContent>
                </Card>

                <Grid container spacing={3}>
                    <Grid item xs={12} lg={8}>
                        <Card
                            role="link"
                            tabIndex={0}
                            aria-label={`${isFr ? 'Lire' : 'Read'} ${featured.title}`}
                            onClick={() => navigate(`/blog/${featured.slug}`)}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter' || event.key === ' ') navigate(`/blog/${featured.slug}`)
                            }}
                            sx={{ ...cardSx, cursor: 'pointer', overflow: 'hidden' }}
                        >
                            <Box role="img" aria-label={`${featured.title} article cover`} sx={{ height: { xs: 260, md: 380 }, backgroundImage: `linear-gradient(180deg, rgba(9,6,27,0.06), rgba(9,6,27,0.34)), url(${featured.hero})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
                            <CardContent sx={{ p: { xs: 3, md: 3.5 } }}>
                                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 1.3 }}>
                                    <Chip label={featured.category} size="small" sx={{ bgcolor: 'rgba(255,138,0,0.12)', color: ORANGE, fontWeight: 800 }} />
                                    <Chip label={featured.tag} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.08)', color: '#fff', fontWeight: 800 }} />
                                    <Stack direction="row" spacing={0.8} alignItems="center" sx={{ color: 'rgba(255,255,255,0.62)' }}>
                                        <CalendarIcon sx={{ fontSize: 17 }} />
                                        <Typography variant="body2">{featured.date}</Typography>
                                    </Stack>
                                </Stack>

                                <Typography component="h2" sx={{ color: '#fff', fontWeight: 950, fontSize: { xs: 28, md: 40 }, lineHeight: 1.06 }}>
                                    {featured.title}
                                </Typography>

                                <Typography sx={{ mt: 1.3, color: 'rgba(255,255,255,0.76)', maxWidth: 760 }}>
                                    {featured.excerpt}
                                </Typography>

                                <Stack direction="row" spacing={0.9} alignItems="center" sx={{ mt: 2.2, color: ORANGE }}>
                                    <Typography sx={{ fontWeight: 900 }}>{isFr ? 'Lire l article' : 'Read article'}</Typography>
                                    <ArrowForwardIcon sx={{ fontSize: 18 }} />
                                </Stack>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} lg={4}>
                        <Stack spacing={3}>
                            <Card sx={cardSx}>
                                <CardContent sx={{ p: 3 }}>
                                    <Stack direction="row" spacing={1.1} alignItems="center" sx={{ mb: 1.3 }}>
                                        <TrendIcon sx={{ color: ORANGE }} />
                                        <Typography sx={{ color: '#fff', fontWeight: 950, fontSize: 22 }}>
                                            {isFr ? 'Dernieres lectures' : 'Latest reads'}
                                        </Typography>
                                    </Stack>
                                    <Stack spacing={1.5}>
                                        {latest.map((post) => (
                                            <Box
                                                key={post.slug}
                                                role="link"
                                                tabIndex={0}
                                                aria-label={`${isFr ? 'Lire' : 'Read'} ${post.title}`}
                                                onClick={() => navigate(`/blog/${post.slug}`)}
                                                onKeyDown={(event) => {
                                                    if (event.key === 'Enter' || event.key === ' ') navigate(`/blog/${post.slug}`)
                                                }}
                                                sx={{ cursor: 'pointer', p: 1.5, borderRadius: 4, bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', '&:hover': { bgcolor: 'rgba(255,255,255,0.05)' } }}
                                            >
                                                <Typography sx={{ color: ORANGE, fontSize: 12, fontWeight: 900 }}>{post.category}</Typography>
                                                <Typography sx={{ color: '#fff', fontWeight: 900, mt: 0.4, lineHeight: 1.2 }}>{post.title}</Typography>
                                                <Typography sx={{ color: 'rgba(255,255,255,0.62)', fontSize: 13.5, mt: 0.7 }}>{post.excerpt}</Typography>
                                                <Stack direction="row" spacing={0.8} alignItems="center" sx={{ color: 'rgba(255,255,255,0.55)', mt: 1 }}>
                                                    <CalendarIcon sx={{ fontSize: 16 }} />
                                                    <Typography variant="body2">{post.date}</Typography>
                                                </Stack>
                                            </Box>
                                        ))}
                                    </Stack>
                                </CardContent>
                            </Card>

                            <Card sx={cardSx}>
                                <CardContent sx={{ p: 3 }}>
                                    <Typography sx={{ color: '#fff', fontWeight: 950, fontSize: 22, mb: 1.2 }}>
                                        {isFr ? 'Ligne editoriale' : 'Editorial focus'}
                                    </Typography>
                                    <Stack spacing={1.1}>
                                        {[
                                            isFr
                                                ? 'Paiements diaspora et transparence reelle du change'
                                                : 'Diaspora payments and real-world FX transparency',
                                            isFr
                                                ? 'Cas d usage commerces et factures a travers les corridors africains'
                                                : 'Business and bill-pay use cases across African corridors',
                                            isFr
                                                ? 'Confiance, preuve, et conception financiere du quotidien'
                                                : 'Trust, proof, and financial daily-life design',
                                        ].map((item) => (
                                            <Box key={item} sx={{ p: 1.4, borderRadius: 4, bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                                                <Typography sx={{ color: 'rgba(255,255,255,0.76)' }}>{item}</Typography>
                                            </Box>
                                        ))}
                                    </Stack>
                                    <Button variant="outlined" fullWidth onClick={() => navigate('/about')} sx={{ mt: 2.2, borderRadius: 999, textTransform: 'none', fontWeight: 900, color: '#fff', borderColor: 'rgba(255,255,255,0.18)' }}>
                                        {isFr ? 'En savoir plus sur MojoPayr' : 'Learn about MojoPayr'}
                                    </Button>
                                </CardContent>
                            </Card>
                        </Stack>
                    </Grid>
                </Grid>
            </Container>
        </Box>
    )
}

export default Blog




